import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createRealtimeChannelName, logRealtimeIssue, removeRealtimeChannel } from '@/lib/realtime';

/**
 * Global realtime subscriptions for tables that don't have
 * their own dedicated hook with a subscription (profiles, notifications).
 * Mount once near the app root so every view stays in sync.
 */
export const useRealtimeSync = () => {
  const queryClient = useQueryClient();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const invalidate = (...keys: string[][]) => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        keys.forEach((k) => queryClient.invalidateQueries({ queryKey: k }));
      }, 500);
    };

    try {
      const channel = supabase
        .channel(createRealtimeChannelName('global-realtime-sync'))
        .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, () => {
          invalidate(['profiles'], ['staff'], ['clients']);
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications' }, () => {
          invalidate(['notifications']);
        })
        .subscribe((_status, error) => {
          if (error) logRealtimeIssue('Global', error);
        });

      return () => {
        clearTimeout(debounceRef.current);
        removeRealtimeChannel(channel);
      };
    } catch (error) {
      logRealtimeIssue('Global', error);
    }
  }, [queryClient]);
};
